import React, { useMemo } from 'react';
import {
    Form,
    InputNumber,
    Input,
    Empty,
    Spin,
    DatePicker,
    Alert,
    Dropdown,
    Tag,
    Tooltip,
} from 'antd';
import {
    DollarOutlined,
    SettingOutlined,
    ArrowDownOutlined,
    TeamOutlined,
    CalendarOutlined,
    SearchOutlined,
    MoreOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    PlusCircleOutlined,
    MinusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSalary, formatCurrency } from '../hooks/useSalary';
import { getSalaryColumns } from '../columns/salaryColumns';
import { Button } from '@/components/ui/Button';
import Table from '@/components/ui/table/Table';
import AppModal from '@/pages/inside/tenant/AppModal';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import { SliderTab } from '@/components/ui/SliderTab';
import { RefreshCwIcon } from '@/components/ui/RefreshCwIcon';
import {
    RolesSearchWrapper,
    RolesSearchInput,
    TenantToolbarIconBtn,
    CreateRoleModalBody,
    CreateRoleModalFooter,
} from '@/pages/inside/tenant/style';

const statusConfig = {
    draft: { color: 'orange', label: 'Draft', bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b', border: 'rgba(245, 158, 11, 0.2)' },
    pending: { color: 'purple', label: 'Waiting Approval', bg: 'rgba(147, 51, 234, 0.1)', text: '#a855f7', border: 'rgba(147, 51, 234, 0.2)' },
    disbursed: { color: 'green', label: 'Disbursed', bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981', border: 'rgba(16, 185, 129, 0.2)' },
    finalized: { color: 'green', label: 'Disbursed', bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981', border: 'rgba(16, 185, 129, 0.2)' },
};

const SalaryDistribution = () => {
    const {
        canView,
        canConfigure,
        canUpdate,
        selectedMonth,
        setSelectedMonth,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        employees,
        config,
        summaryStats,
        isLoading,
        isFetching,
        isError,
        refetch,
        configModalOpen,
        setConfigModalOpen,
        handleOpenConfigModal,
        handleConfigSubmit,
        configForm,
        configMutation,
        viewingRow,
        viewModalOpen,
        setViewModalOpen,
        handleViewRow,
        handleViewSubmit,
        viewForm,
        updateMonthlyMutation,
        handleMarkStatus,
        handleBulkDisbursement,
        bulkStatusMutation,
    } = useSalary();

    const STATUS_TABS = [
        { key: 'all', label: 'All Salaries' },
        { key: 'draft', label: 'Draft' },
        { key: 'pending', label: 'Waiting Approval' },
        { key: 'disbursed', label: 'Disbursed' },
    ];

    const kpis = useMemo(() => {
        const startDay = parseInt(config?.salaryCycleStartDay ?? 1, 10);
        let dateRangeStr = dayjs(selectedMonth, 'YYYY-MM').format('MMMM YYYY');
        
        if (startDay !== 1) {
            const start = dayjs(selectedMonth, 'YYYY-MM').date(startDay);
            const end = start.add(1, 'month').subtract(1, 'day');
            dateRangeStr = `${start.format('D MMM')} to ${end.format('D MMM YYYY')}`;
        }

        return [
            {
                label: 'Employees',
                value: summaryStats.employeeCount,
                icon: <TeamOutlined style={{ color: '#818cf8' }} />,
                subValue: 'Active members',
            },
            {
                label: 'Total Payroll',
                value: formatCurrency(summaryStats.totalPayroll),
                icon: <DollarOutlined style={{ color: '#34d399' }} />,
                subValue: `for ${dateRangeStr}`,
            },
            {
                label: 'Total Deductions',
                value: formatCurrency(summaryStats.totalDeductions),
                icon: <ArrowDownOutlined style={{ color: '#f87171' }} />,
                subValue: 'Absent + late penalties',
            },
            {
                label: 'Avg Salary',
                value: formatCurrency(summaryStats.avgSalary),
                icon: <CalendarOutlined style={{ color: '#fbbf24' }} />,
                subValue: 'Per employee',
            },
        ];
    }, [summaryStats, selectedMonth, config]);

    // Table Columns Definition
    const columns = useMemo(
        () => getSalaryColumns({ handleViewRow, handleMarkStatus, canUpdate }),
        [canUpdate, handleViewRow, handleMarkStatus]
    );

    // Live calculation values inside View Modal
    const additionsVal = Form.useWatch('additions', viewForm) || 0;
    const customDeductionsVal = Form.useWatch('customDeductions', viewForm) || 0;

    const modalCalculations = useMemo(() => {
        if (!viewingRow) return null;
        const base = parseFloat(viewingRow.baseSalary || 0);
        const scheduledDays = 22;
        const dailyRate = base > 0 ? base / scheduledDays : 0;
        const earnedBase = viewingRow.presentDays * dailyRate;
        const absentDeduct = parseFloat(viewingRow.deductions || 0);
        const netCalculated = Math.max(0, earnedBase + additionsVal - customDeductionsVal);
        return {
            base,
            dailyRate,
            earnedBase,
            absentDeduct,
            additionsVal,
            customDeductionsVal,
            netCalculated,
        };
    }, [viewingRow, additionsVal, customDeductionsVal]);

    if (!canView) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message="Access Denied"
                    description="You do not have permission to view salary distribution."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <ListBodyLayout
            title={
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DollarOutlined style={{ color: '#34d399' }} />
                    Salary Distribution
                </span>
            }
            description="Monthly payroll calculation, attendance verification, and disbursement"
            actions={
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <DatePicker
                        picker="month"
                        value={dayjs(selectedMonth, 'YYYY-MM')}
                        onChange={(val) => val && setSelectedMonth(val.format('YYYY-MM'))}
                        format="MMMM YYYY"
                        allowClear={false}
                        style={{
                            borderRadius: 10,
                            height: 38,
                            fontWeight: 500,
                        }}
                    />
                    <Button
                        $primary={false}
                        icon={<SettingOutlined />}
                        onClick={handleOpenConfigModal}
                        permissionDenied={!canConfigure}
                        $padding="9px 16px"
                        $size="13px"
                    >
                        Configure
                    </Button>
                    <Button
                        $primary={true}
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleBulkDisbursement('pending')}
                        loading={bulkStatusMutation.isPending}
                        permissionDenied={!canUpdate}
                        $padding="9px 18px"
                        $size="13px"
                    >
                        Process Disbursement
                    </Button>
                </div>
            }
            kpiCards={kpis}
        >
            {/* Toolbar Filter Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <SliderTab
                    tabs={STATUS_TABS}
                    activeTabKey={statusTab}
                    onTabChange={setStatusTab}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            type="text"
                            placeholder="Search employee by name or email..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                    <TenantToolbarIconBtn title="Refresh" onClick={() => refetch()}>
                        <RefreshCwIcon className={isFetching ? 'spin' : ''} />
                    </TenantToolbarIconBtn>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
            ) : isError ? (
                <Alert message="Failed to load salary data" type="error" showIcon />
            ) : employees.length === 0 ? (
                <Empty description={<span style={{ color: 'var(--ant-color-text-tertiary)' }}>No employee salary data found</span>} />
            ) : (
                <Table
                    columns={columns}
                    dataSource={employees.map((e, i) => ({ ...e, key: e.userId ?? i }))}
                    pagination={false}
                    style={{ borderRadius: 16, overflow: 'hidden' }}
                />
            )}

            {/* Configure Modal */}
            <AppModal
                size="md"
                open={configModalOpen}
                onCancel={() => setConfigModalOpen(false)}
                title="Salary Configuration"
                footer={null}
            >
                <CreateRoleModalBody>
                    <p style={{ color: 'var(--ant-color-text-secondary)', fontSize: 13, marginBottom: 20 }}>
                        Set deduction rules per absent or late day.
                    </p>
                    <Form form={configForm} layout="vertical" onFinish={handleConfigSubmit}>
                        <Form.Item
                            label="Absent Deduction (days per absent day)"
                            name="absentDeductionRate"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber min={0} max={30} step={0.5} style={{ width: '100%' }} placeholder="1.0" />
                        </Form.Item>
                        <Form.Item
                            label="Late Deduction (days per late day)"
                            name="lateDeductionRate"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber min={0} max={30} step={0.25} style={{ width: '100%' }} placeholder="0.0" />
                        </Form.Item>
                        <Form.Item
                            label="Salary Cycle Cut-off Day (1 - 31)"
                            name="salaryCycleStartDay"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber min={1} max={31} step={1} style={{ width: '100%' }} placeholder="15" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={() => setConfigModalOpen(false)} $padding="10px 16px" $size="14px">Cancel</Button>
                    <Button $primary onClick={() => configForm.submit()} loading={configMutation.isPending} $padding="10px 16px" $size="14px">Save Config</Button>
                </CreateRoleModalFooter>
            </AppModal>

            {/* Professional View & Summary Calculation Modal */}
            <AppModal
                size="md"
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                title={`Payroll Summary — ${viewingRow?.name ?? ''}`}
                footer={null}
            >
                <CreateRoleModalBody>
                    {viewingRow && (
                        <div>
                            {/* Employee Info Header */}
                            <div style={{
                                background: 'var(--ant-color-fill-alter, rgba(0, 0, 0, 0.02))',
                                borderRadius: 12,
                                padding: 16,
                                marginBottom: 16,
                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
                            }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--ant-color-text)' }}>{viewingRow.name}</div>
                                    <div style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>{viewingRow.email}</div>
                                </div>
                                <div>
                                    <Tag color={statusConfig[viewingRow.status]?.color || 'default'} style={{ borderRadius: 12, padding: '2px 10px' }}>
                                        {statusConfig[viewingRow.status]?.label || 'Draft'}
                                    </Tag>
                                </div>
                            </div>

                            {/* Attendance Quick Metrics */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                                <div style={{ background: 'rgba(16, 185, 129, 0.08)', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                    <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Present Days</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#10b981' }}>{viewingRow.presentDays}d</div>
                                </div>
                                <div style={{ background: 'rgba(239, 68, 68, 0.08)', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                    <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Absent Days</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{viewingRow.absentDays}d</div>
                                </div>
                                <div style={{ background: 'rgba(245, 158, 11, 0.08)', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                                    <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>Late Days</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{viewingRow.lateDays}d</div>
                                </div>
                            </div>

                            {/* Detailed Step-by-Step Calculation Breakdown Card */}
                            {modalCalculations && (
                                <div style={{
                                    background: 'var(--ant-color-bg-container, #fff)',
                                    borderRadius: 12,
                                    padding: 16,
                                    marginBottom: 16,
                                    border: '1px solid var(--ant-color-border, #d9d9d9)',
                                    fontSize: 13,
                                }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--ant-color-text)' }}>
                                        Calculation Ledger Breakdown
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ color: 'var(--ant-color-text-secondary)' }}>Monthly Base Salary</span>
                                        <span style={{ fontWeight: 600 }}>{formatCurrency(modalCalculations.base)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ color: 'var(--ant-color-text-secondary)' }}>Daily Working Rate (22 Working Days)</span>
                                        <span style={{ color: 'var(--ant-color-text-secondary)' }}>{formatCurrency(modalCalculations.dailyRate)} / day</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ color: 'var(--ant-color-text-secondary)' }}>Earned Salary ({viewingRow.presentDays} Days Present)</span>
                                        <span style={{ fontWeight: 600, color: '#10b981' }}>+ {formatCurrency(modalCalculations.earnedBase)}</span>
                                    </div>
                                    {modalCalculations.absentDeduct > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span style={{ color: 'var(--ant-color-text-secondary)' }}>Absent Deductions ({viewingRow.absentDays} Days)</span>
                                            <span style={{ color: '#ef4444', fontWeight: 600 }}>- {formatCurrency(modalCalculations.absentDeduct)}</span>
                                        </div>
                                    )}
                                    <hr style={{ border: 'none', borderTop: '1px dashed var(--ant-color-border-secondary, #eee)', margin: '12px 0' }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 700, fontSize: 14 }}>Net Payable Salary</span>
                                        <span style={{ fontWeight: 700, fontSize: 18, color: '#10b981' }}>
                                            {formatCurrency(modalCalculations.netCalculated)}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Interactive Form for Additions, Custom Deductions & Notes */}
                            <Form form={viewForm} layout="vertical">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <Form.Item label="Additions / Bonus ($)" name="additions" initialValue={0}>
                                        <InputNumber min={0} step={100} style={{ width: '100%' }} prefix={<PlusCircleOutlined style={{ color: '#10b981' }} />} />
                                    </Form.Item>
                                    <Form.Item label="Custom Deduction ($)" name="customDeductions" initialValue={0}>
                                        <InputNumber min={0} step={100} style={{ width: '100%' }} prefix={<MinusCircleOutlined style={{ color: '#ef4444' }} />} />
                                    </Form.Item>
                                </div>
                                <Form.Item label="Adjusted Actual Salary ($)" name="actualSalary">
                                    <InputNumber min={0} step={500} style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item label="Payroll Ledger Notes" name="notes">
                                    <Input.TextArea rows={2} placeholder="Add payment reference or approval notes..." />
                                </Form.Item>
                            </Form>
                        </div>
                    )}
                </CreateRoleModalBody>
                <CreateRoleModalFooter style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Button $primary={false} onClick={() => setViewModalOpen(false)} $padding="10px 16px" $size="13px">Cancel</Button>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            $primary={false}
                            onClick={() => handleViewSubmit(viewForm.getFieldsValue(), 'pending')}
                            loading={updateMonthlyMutation.isPending}
                            $padding="10px 14px"
                            $size="13px"
                        >
                            Mark Pending
                        </Button>
                        <Button
                            $primary={true}
                            onClick={() => handleViewSubmit(viewForm.getFieldsValue(), 'disbursed')}
                            loading={updateMonthlyMutation.isPending}
                            $padding="10px 16px"
                            $size="13px"
                        >
                            Disburse Salary
                        </Button>
                    </div>
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default SalaryDistribution;
